import React, { useState, useMemo, useEffect } from 'react';
import {
    ChevronDown, ChevronUp, Plus, MoreHorizontal, Video, Users,
    Clock, MapPin, X, CheckCircle2, ChevronLeft, ChevronRight,
    Trash2, Calendar as CalendarIcon, AlignLeft, Flag, LayoutList
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCalendar } from '../../hooks/useCalendar';
import { formatTime, formatDate, formatLongDate } from '../../utils';

const CalendarView = ({ user, isSidePanel }) => {
    const {
        events,
        todos,
        addEvent,
        deleteEvent,
        addTodo,
        updateTodo,
        toggleTodo,
        deleteTodo,
        isLoading
    } = useCalendar(user);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTodoModal, setShowTodoModal] = useState(false);
    const [editingTodo, setEditingTodo] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [newTodoText, setNewTodoText] = useState('');
    const [showMenu, setShowMenu] = useState(false);
    const [showDetailTaskModal, setShowDetailTaskModal] = useState(false);
    const [notification, setNotification] = useState(null);
    const [completingId, setCompletingId] = useState(null);

    // Sync editingTodo and selectedEvent with latest todos
    useEffect(() => {
        if (editingTodo) {
            const latest = todos.find(t => t.id === editingTodo.id);
            if (latest && JSON.stringify(latest) !== JSON.stringify(editingTodo)) {
                setEditingTodo(latest);
            }
        }
        if (selectedEvent && selectedEvent.type === 'todo') {
            const latest = todos.find(t => t.id === selectedEvent.id);
            if (latest && JSON.stringify(latest) !== JSON.stringify(selectedEvent)) {
                setSelectedEvent({ ...latest, type: 'todo' });
            }
        }
    }, [todos, editingTodo, selectedEvent]);

    // Date Helpers
    const getVisibleDays = (date) => {
        const start = new Date(date);

        if (isExpanded) {
            // Month view: Start from the beginning of the month, then adjust to the start of the week
            start.setDate(1);
            start.setDate(start.getDate() - start.getDay());

            // Return 6 weeks (42 days) to cover the full month view
            return Array.from({ length: 42 }).map((_, i) => {
                const d = new Date(start);
                d.setDate(d.getDate() + i);
                return d;
            });
        } else {
            // Compressed view: Start from the beginning of the week of the selected date
            start.setDate(start.getDate() - start.getDay());
            return Array.from({ length: 14 }).map((_, i) => {
                const d = new Date(start);
                d.setDate(d.getDate() + i);
                return d;
            });
        }
    };

    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const isSameDay = (d1, d2) => {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    };

    const isToday = (date) => isSameDay(date, new Date());

    // Grouping events for the agenda
    const agendaDays = useMemo(() => {
        const days = [];
        const start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);

        for (let i = 0; i < 7; i++) {
            const day = new Date(start);
            day.setDate(day.getDate() + i);

            const dayEvents = events.filter(event => {
                const eventDate = new Date(event.startTime);
                return isSameDay(eventDate, day);
            }).map(e => ({ ...e, type: 'event' }));

            const dayTodos = todos.filter(todo => {
                const todoDate = new Date(todo.dueDate);
                return isSameDay(todoDate, day);
            }).map(t => ({ ...t, type: 'todo' }));

            days.push({
                date: day,
                events: [...dayEvents, ...dayTodos].sort((a, b) => {
                    // Sort by time if available, otherwise loose sort
                    const timeA = a.startTime ? new Date(a.startTime).getTime() : new Date(a.dueDate || 0).getTime();
                    const timeB = b.startTime ? new Date(b.startTime).getTime() : new Date(b.dueDate || 0).getTime();
                    return timeA - timeB;
                }),
                label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : null
            });
        }
        return days;
    }, [events, todos, selectedDate]);

    // Avatar Component for participants
    const ParticipantAvatars = ({ attendees }) => {
        if (!attendees) return null;
        const list = attendees.split(',').map(s => s.trim()).filter(s => s);
        return (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', gap: '4px' }}>
                {list.slice(0, 4).map((name, i) => (
                    <div key={i} style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: i % 2 === 0 ? '#005a9e' : '#4b5563',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        position: 'relative'
                    }}>
                        {name[0]?.toUpperCase()}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#22c55e',
                            border: '1px solid white'
                        }} />
                    </div>
                ))}
                {list.length > 4 && (
                    <div style={{
                        fontSize: '0.8rem',
                        color: 'var(--text-muted)',
                        marginLeft: '4px',
                        fontWeight: '500'
                    }}>
                        +{list.length - 4}
                    </div>
                )}
            </div>
        );
    };

    const EventCard = ({ event }) => (
        <motion.div
            whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
            onClick={() => {
                setSelectedEvent(event);
                setEditingTodo(null);
            }}
            style={{
                display: 'flex',
                padding: '16px 20px',
                cursor: 'pointer',
                gap: '20px',
                position: 'relative'
            }}
        >
            {/* Time Column */}
            <div style={{ minWidth: '80px', paddingTop: '4px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-main)' }}>
                    {formatTime(event.startTime)}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    30m
                </div>
            </div>

            {/* Event Details */}
            <div style={{
                flex: 1,
                borderLeft: `5px solid ${event.color || '#f97316'}`,
                paddingLeft: '16px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>
                    {event.title}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {event.isMeeting ? 'Meeting' : (event.location || 'No location')}
                </div>
                <ParticipantAvatars attendees={event.attendees} />
            </div>
        </motion.div>
    );

    useEffect(() => {
        console.log('Current todos in state:', todos);
    }, [todos]);

    const handleAddTodo = () => {
        if (newTodoText.trim()) {
            addTodo(newTodoText);
            setNewTodoText('');
        }
    };

    const handlePrev = () => {
        const newDate = new Date(selectedDate);
        if (isExpanded) {
            newDate.setMonth(newDate.getMonth() - 1);
        } else {
            newDate.setDate(newDate.getDate() - 7);
        }
        setSelectedDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(selectedDate);
        if (isExpanded) {
            newDate.setMonth(newDate.getMonth() + 1);
        } else {
            newDate.setDate(newDate.getDate() + 7);
        }
        setSelectedDate(newDate);
    };

    const isMobileUI = window.innerWidth <= 768;

    return (
        <div style={{
            display: 'flex',
            flexDirection: isMobileUI ? 'column' : 'row',
            height: '100%',
            width: '100%',
            backgroundColor: 'var(--bg-main)',
            overflow: 'hidden'
        }}>
            {/* Calendar Column */}
            <div style={{
                flex: isMobileUI ? 'none' : '0 0 320px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--bg-main)',
                height: isMobileUI ? 'auto' : '100%',
                maxHeight: isMobileUI ? '100%' : 'none',
                borderRight: '1px solid var(--border-glass)',
                overflowY: isMobileUI ? 'auto' : 'visible'
            }}>
                <div style={{ padding: '24px 20px 10px' }}>
                    {!isSidePanel && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setShowCreateModal(true)}
                            className="btn-primary"
                            style={{
                                width: 'fit-content',
                                padding: '8px 20px',
                                justifyContent: 'center',
                                borderRadius: '12px',
                                background: 'var(--primary)',
                                color: '#ffffff',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                marginBottom: '24px'
                            }}
                        >
                            <CalendarIcon size={18} strokeWidth={2.5} />
                            <span style={{ marginLeft: '8px' }}>New Event</span>
                        </motion.button>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--text-main)', margin: 0 }}>
                                {monthNames[selectedDate.getMonth()]}
                            </h2>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <div onClick={handlePrev} style={{ cursor: 'pointer', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                    <ChevronLeft size={16} />
                                </div>
                                <div onClick={handleNext} style={{ cursor: 'pointer', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                    <ChevronRight size={16} />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', color: 'var(--text-muted)', position: 'relative' }}>
                            {/* Menu removed per user request */}
                        </div>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        textAlign: 'center',
                        marginBottom: '10px'
                    }}>
                        {weekDays.map((day, idx) => (
                            <span key={idx} style={{ fontSize: '0.7rem', fontWeight: '600', color: 'var(--text-dim)' }}>{day}</span>
                        ))}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px 0',
                        textAlign: 'center'
                    }}>
                        {getVisibleDays(selectedDate).map((date, i) => {
                            const isSelected = isSameDay(date, selectedDate);
                            const isCurrentMonth = date.getMonth() === selectedDate.getMonth();

                            return (
                                <div
                                    key={i}
                                    onClick={() => setSelectedDate(date)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: '4px 0',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.8rem',
                                        fontWeight: isSelected ? '700' : '500',
                                        backgroundColor: isSelected ? 'var(--primary)' : 'transparent',
                                        color: isSelected ? 'white' : (isCurrentMonth ? 'var(--text-main)' : 'var(--text-dim)'),
                                        transition: 'all 0.2s'
                                    }}>
                                        {date.getDate()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                        <div
                            onClick={() => setIsExpanded(!isExpanded)}
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--bg-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                border: '1px solid var(--border-glass)',
                                transition: 'all 0.2s ease'
                            }}>
                            {isExpanded ? (
                                <ChevronUp size={14} color="var(--text-muted)" />
                            ) : (
                                <ChevronDown size={14} color="var(--text-muted)" />
                            )}
                        </div>
                    </div>
                </div>

                <div className="scrollbar-hide" style={{
                    flex: 1,
                    backgroundColor: 'var(--bg-main)',
                    borderTopRightRadius: '24px',
                    borderTopLeftRadius: '24px',
                    marginTop: '8px',
                    overflowY: 'auto',
                    minHeight: 0
                }}>
                    {agendaDays.map(({ date, events: dayEvents, label }) => (
                        <div key={date.toISOString()}>
                            <div style={{
                                padding: '10px 20px',
                                backgroundColor: 'var(--bg-secondary)',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                color: 'var(--primary)',
                                display: 'flex',
                                gap: '6px',
                                alignItems: 'center',
                                borderBottom: '1px solid var(--border-glass)'
                            }}>
                                {label && <span>{label} •</span>}
                                <span>{new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }).format(date)}</span>
                            </div>

                            {dayEvents.length > 0 ? (
                                dayEvents.map(item => (
                                    item.type === 'event' ? (
                                        <EventCard key={`e-${item.id}`} event={item} />
                                    ) : (
                                        <div
                                            key={`t-${item.id}`}
                                            onClick={() => {
                                                if (item.dueDate) {
                                                    setSelectedDate(new Date(item.dueDate));
                                                }
                                                setEditingTodo(item);
                                                setSelectedEvent(null);
                                                if (isMobileUI || isSidePanel) {
                                                    setShowDetailTaskModal(true);
                                                }
                                            }}
                                            style={{
                                                padding: '12px 20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                borderBottom: '1px solid var(--border-glass)',
                                                backgroundColor: 'white',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleTodo(item);
                                                }}
                                                style={{ cursor: 'pointer', color: item.completed ? '#22c55e' : '#d1d5db' }}
                                            >
                                                <CheckCircle2 size={20} fill={item.completed ? "#22c55e" : "transparent"} color={item.completed ? "#22c55e" : "#d1d5db"} />
                                            </div>
                                            <span style={{
                                                flex: 1,
                                                textDecoration: item.completed ? 'line-through' : 'none',
                                                color: item.completed ? '#9ca3af' : '#374151',
                                                fontSize: '0.9rem',
                                                fontWeight: '500'
                                            }}>
                                                {item.title}
                                            </span>
                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '12px' }}>
                                                Task
                                            </div>
                                        </div>
                                    )
                                ))
                            ) : (
                                <div style={{ padding: '12px 20px', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                                    No events
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Large Detail Pane (Hidden in SidePanel mode or Mobile) */}
            {!isSidePanel && !isMobileUI && (
                <div style={{ flex: 1, backgroundColor: 'var(--bg-main)', display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border-glass)' }}>
                    <AnimatePresence mode="wait">
                        {selectedEvent ? (
                            <motion.div
                                key={`event-${selectedEvent.id}`}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.98 }}
                                style={{ padding: '48px', maxWidth: '700px' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
                                    <div style={{ width: '6px', height: '32px', borderRadius: '3px', backgroundColor: selectedEvent.color || '#f97316' }} />
                                    <div>
                                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-main)', margin: 0 }}>{selectedEvent.title}</h3>
                                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '4px' }}>
                                            {formatLongDate(selectedEvent.startTime)}
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '32px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(0, 120, 212, 0.1)' }}>
                                            <Video size={18} color="#0078d4" />
                                        </div>
                                        <span style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--text-main)' }}>Meeting</span>
                                    </div>

                                    {selectedEvent.attendees && (
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                            <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(0, 120, 212, 0.1)' }}>
                                                <Users size={18} color="#0078d4" />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Participants</p>
                                                <ParticipantAvatars attendees={selectedEvent.attendees} />
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(0, 120, 212, 0.1)' }}>
                                            <Clock size={18} color="#0078d4" />
                                        </div>
                                        <div>
                                            <p style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '2px', letterSpacing: '0.5px' }}>Time Slot</p>
                                            <span style={{ fontSize: '0.95rem', color: 'var(--text-main)' }}>30 Minutes</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '20px' }}>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => {
                                            if (selectedEvent.meetingLink) {
                                                const url = selectedEvent.meetingLink.startsWith('http')
                                                    ? selectedEvent.meetingLink
                                                    : `https://${selectedEvent.meetingLink}`;
                                                window.open(url, '_blank');
                                            }
                                        }}
                                        style={{
                                            padding: '16px 48px',
                                            backgroundColor: '#0078d4',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '16px',
                                            fontWeight: '800',
                                            fontSize: '1.1rem',
                                            boxShadow: '0 8px 16px rgba(0, 120, 212, 0.25)',
                                            cursor: 'pointer',
                                            opacity: selectedEvent.meetingLink ? 1 : 0.5,
                                            pointerEvents: selectedEvent.meetingLink ? 'auto' : 'none'
                                        }}
                                    >
                                        Join Meeting
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02, backgroundColor: '#fee2e2' }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => {
                                            deleteEvent(selectedEvent.id);
                                            setSelectedEvent(null);
                                        }}
                                        style={{
                                            padding: '16px 24px',
                                            backgroundColor: 'white',
                                            color: '#ef4444',
                                            border: '1px solid #fee2e2',
                                            borderRadius: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Trash2 size={24} />
                                    </motion.button>
                                </div>
                            </motion.div>
                        ) : editingTodo ? (
                            <motion.div
                                key={`todo-${editingTodo.id}`}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.98 }}
                                style={{ padding: '48px', maxWidth: '700px' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
                                    <div
                                        onClick={async () => {
                                            if (!editingTodo.completed) {
                                                setCompletingId(editingTodo.id);
                                                setNotification('Task Checked!');
                                                setTimeout(async () => {
                                                    await toggleTodo(editingTodo);
                                                    setCompletingId(null);
                                                    setTimeout(() => setNotification(null), 2000);
                                                }, 400);
                                            } else {
                                                await toggleTodo(editingTodo);
                                            }
                                        }}
                                        style={{ 
                                            cursor: 'pointer', 
                                            color: completingId === editingTodo.id || editingTodo.completed ? '#22c55e' : '#d1d5db',
                                            transition: 'color 0.2s'
                                        }}
                                    >
                                        <CheckCircle2 size={24} fill={completingId === editingTodo.id || editingTodo.completed ? "#22c55e" : "transparent"} color={completingId === editingTodo.id || editingTodo.completed ? "#22c55e" : "#d1d5db"} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{
                                            fontSize: '1.5rem',
                                            fontWeight: '700',
                                            color: editingTodo.completed ? 'var(--text-dim)' : 'var(--text-main)',
                                            margin: 0,
                                            textDecoration: editingTodo.completed ? 'line-through' : 'none'
                                        }}>
                                            {editingTodo.title}
                                        </h3>
                                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '4px' }}>
                                            {editingTodo.dueDate ? formatLongDate(editingTodo.dueDate) : 'No due date'}
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '32px' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(65, 105, 225, 0.1)' }}>
                                            <AlignLeft size={18} color="var(--primary)" />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Description</p>
                                            <div style={{
                                                fontSize: '0.95rem',
                                                color: 'var(--text-main)',
                                                lineHeight: '1.5',
                                                background: 'var(--bg-secondary)',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                minHeight: '80px',
                                                border: '1px solid var(--border-glass)'
                                            }}>
                                                {editingTodo.description || 'No description provided.'}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.1)' }}>
                                            <Flag size={18} color="#ef4444" />
                                        </div>
                                        <div>
                                            <p style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '2px', letterSpacing: '0.5px' }}>Priority</p>
                                            <span style={{
                                                fontSize: '0.95rem',
                                                fontWeight: '700',
                                                color: editingTodo.priority === 'High' ? '#ef4444' : editingTodo.priority === 'Medium' ? '#f59e0b' : '#10b981'
                                            }}>
                                                {editingTodo.priority || 'Normal'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '20px' }}>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={async () => {
                                            if (!editingTodo.completed) {
                                                setCompletingId(editingTodo.id);
                                                setNotification('Task Checked!');
                                                setTimeout(async () => {
                                                    await toggleTodo(editingTodo);
                                                    setCompletingId(null);
                                                    setTimeout(() => setNotification(null), 2000);
                                                }, 400);
                                            } else {
                                                await toggleTodo(editingTodo);
                                            }
                                        }}
                                        style={{
                                            padding: '10px 24px',
                                            backgroundColor: (completingId === editingTodo.id || editingTodo.completed) ? '#22c55e' : 'var(--primary)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '12px',
                                            fontWeight: '700',
                                            fontSize: '0.9rem',
                                            boxShadow: (completingId === editingTodo.id || editingTodo.completed) ? '0 4px 8px rgba(34, 197, 94, 0.2)' : '0 4px 8px var(--primary-glow)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {(completingId === editingTodo.id || editingTodo.completed) ? 'Checked!' : 'Mark as Completed'}
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02, backgroundColor: '#fee2e2' }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => {
                                            deleteTodo(editingTodo.id);
                                            setEditingTodo(null);
                                        }}
                                        style={{
                                            padding: '16px 24px',
                                            backgroundColor: 'white',
                                            color: '#ef4444',
                                            border: '1px solid #fee2e2',
                                            borderRadius: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Trash2 size={24} />
                                    </motion.button>
                                </div>
                            </motion.div>
                        ) : (
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-muted)',
                                opacity: 0.3
                            }}>
                                <CalendarIcon size={80} strokeWidth={1} style={{ marginBottom: '24px' }} />
                                <p style={{ fontSize: '1.25rem', fontWeight: '500' }}>Click an item to preview details</p>
                            </div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Creation Modal (Simplified but Functional) */}
            <AnimatePresence>
                {showCreateModal && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            style={{
                                width: '100%',
                                maxWidth: '400px',
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '700' }}>New Event</h3>
                                <X size={20} style={{ cursor: 'pointer' }} onClick={() => setShowCreateModal(false)} />
                            </div>
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                addEvent({
                                    title: formData.get('title'),
                                    startTime: new Date(`${formData.get('date')}T${formData.get('time')}`),
                                    endTime: new Date(`${formData.get('date')}T${formData.get('time')}`),
                                    isMeeting: true,
                                    attendees: formData.get('attendees'),
                                    meetingLink: formData.get('meetingLink')
                                });
                                setShowCreateModal(false);
                            }}>
                                <input name="title" placeholder="Event Title" required style={{ width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                                <input name="date" type="date" defaultValue={selectedDate.toISOString().split('T')[0]} required style={{ width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                                <input name="time" type="time" required style={{ width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                                <input name="meetingLink" placeholder="Meeting URL (e.g., Zoom, Teams)" style={{ width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                                <input name="attendees" placeholder="Add participants (comma separated)" style={{ width: '100%', padding: '12px', marginBottom: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                                <button type="submit" style={{ width: '100%', padding: '12px', backgroundColor: '#0078d4', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600' }}>
                                    Create Event
                                </button>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Event Detail Modal (Only for Mobile or Side Panel where background pane isn't visible) */}
            <AnimatePresence>
                {(isMobileUI || isSidePanel) && selectedEvent && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            style={{
                                width: '100%',
                                maxWidth: '400px',
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                borderLeft: `8px solid ${selectedEvent.color || '#f97316'}`
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '4px' }}>{selectedEvent.title}</h3>
                                    <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                        {formatLongDate(selectedEvent.startTime)}
                                    </p>
                                </div>
                                <X size={24} style={{ cursor: 'pointer' }} onClick={() => setSelectedEvent(null)} />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Video size={18} color="#0078d4" />
                                    <span style={{ fontSize: '0.95rem', color: '#111827' }}>Meeting</span>
                                </div>
                                {selectedEvent.attendees && (
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <Users size={18} color="#0078d4" style={{ marginTop: '2px' }} />
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: '0.8rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>Participants</p>
                                            <ParticipantAvatars attendees={selectedEvent.attendees} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => {
                                        if (selectedEvent.meetingLink) {
                                            const url = selectedEvent.meetingLink.startsWith('http')
                                                ? selectedEvent.meetingLink
                                                : `https://${selectedEvent.meetingLink}`;
                                            window.open(url, '_blank');
                                        }
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        backgroundColor: '#0078d4',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        fontWeight: '700',
                                        opacity: selectedEvent.meetingLink ? 1 : 0.5,
                                        pointerEvents: selectedEvent.meetingLink ? 'auto' : 'none'
                                    }}>
                                    Join Now
                                </button>
                                <button
                                    onClick={() => {
                                        deleteEvent(selectedEvent.id);
                                        setSelectedEvent(null);
                                    }}
                                    style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '12px' }}
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Todo List Modal */}
            <AnimatePresence>
                {showTodoModal && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            style={{
                                width: '100%',
                                maxWidth: '400px',
                                height: '500px',
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '700' }}>My Tasks</h3>
                                <X size={20} style={{ cursor: 'pointer' }} onClick={() => setShowTodoModal(false)} />
                            </div>

                            <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', marginBottom: '20px', minHeight: 0 }}>
                                {todos.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: '40px' }}>
                                        No tasks yet. Add one below!
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {todos.map(todo => (
                                            <div key={todo.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: '#f3f4f6' }}>
                                                <div
                                                    onClick={async () => {
                                                        if (!todo.completed) {
                                                            setCompletingId(todo.id);
                                                            setNotification('Task Checked!');
                                                            setTimeout(async () => {
                                                                await toggleTodo(todo);
                                                                setCompletingId(null);
                                                                setTimeout(() => setNotification(null), 2000);
                                                            }, 400);
                                                        } else {
                                                            await toggleTodo(todo);
                                                        }
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        marginTop: '2px'
                                                    }}
                                                >
                                                    <CheckCircle2 size={24} fill={completingId === todo.id ? "#22c55e" : (todo.completed ? "#22c55e" : "transparent")} color={completingId === todo.id || todo.completed ? "#22c55e" : "#d1d5db"} />
                                                </div>

                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <span style={{
                                                        textDecoration: todo.completed ? 'line-through' : 'none',
                                                        color: todo.completed ? '#9ca3af' : '#111827',
                                                        fontWeight: '600',
                                                        fontSize: '1rem'
                                                    }}>
                                                        {todo.title}
                                                    </span>

                                                    {/* Task Details - Only show if not completed to keep completed list clean, or show dimmed */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            {todo.priority && (
                                                                <span style={{
                                                                    fontSize: '0.75rem',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '12px',
                                                                    fontWeight: '600',
                                                                    backgroundColor: todo.priority === 'High' ? '#fee2e2' : todo.priority === 'Medium' ? '#fef3c7' : '#e0f2fe',
                                                                    color: todo.priority === 'High' ? '#991b1b' : todo.priority === 'Medium' ? '#92400e' : '#075985'
                                                                }}>
                                                                    {todo.priority}
                                                                </span>
                                                            )}

                                                            {todo.dueDate && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#6b7280' }}>
                                                                    <CalendarIcon size={12} />
                                                                    <span>{formatDate(todo.dueDate)}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {todo.description && (
                                                            <div style={{
                                                                fontSize: '0.875rem',
                                                                color: '#4b5563',
                                                                lineHeight: '1.4',
                                                                marginTop: '2px'
                                                            }}>
                                                                {todo.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <Trash2
                                                    size={18}
                                                    color="#ef4444"
                                                    style={{ cursor: 'pointer', opacity: 0.6, marginTop: '4px' }}
                                                    onClick={() => deleteTodo(todo.id)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditingTodo(null);
                                        setShowDetailTaskModal(true);
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        backgroundColor: '#0078d4',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontWeight: '600',
                                        fontSize: '1rem',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    <Plus size={20} />
                                    <span>New Task</span>
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )
                }
            </AnimatePresence >

            {/* Detailed Task Creation Modal */}
            < AnimatePresence >
                {showDetailTaskModal && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1010,
                        padding: '20px'
                    }}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            style={{
                                width: '100%',
                                maxWidth: '700px',
                                background: 'white',
                                borderRadius: '12px',
                                padding: '32px',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                            }}
                        >
                            {/* Header / Actions */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>

                                <div style={{ display: 'flex', gap: '16px', color: '#6b7280' }}>
                                    <X size={24} style={{ cursor: 'pointer' }} onClick={() => {
                                        setShowDetailTaskModal(false);
                                        setEditingTodo(null);
                                    }} />
                                </div>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                const todoData = {
                                    title: formData.get('title'),
                                    priority: formData.get('priority'),
                                    dueDate: formData.get('dueDate') ? new Date(formData.get('dueDate')) : new Date(),
                                    description: formData.get('description'),
                                    completed: editingTodo ? editingTodo.completed : false
                                };

                                if (editingTodo) {
                                    updateTodo(editingTodo.id, todoData);
                                } else {
                                    addTodo(todoData);
                                }

                                setShowDetailTaskModal(false);
                                setEditingTodo(null);
                                if (!showTodoModal) setShowTodoModal(true);
                            }}>
                                {/* Title */}
                                <div style={{ marginBottom: '24px' }}>
                                    <input
                                        name="title"
                                        defaultValue={editingTodo?.title || ''}
                                        placeholder="Task Title"
                                        style={{
                                            width: '100%',
                                            fontSize: '1.5rem',
                                            fontWeight: '700',
                                            border: 'none',
                                            outline: 'none',
                                            color: '#111827'
                                        }}
                                        autoFocus
                                    />
                                </div>

                                {/* Metadata Row */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
                                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>in</span>
                                    <div style={{ padding: '4px 12px', background: '#eff6ff', color: '#2563eb', borderRadius: '6px', fontSize: '0.9rem', fontWeight: '500' }}>Personal tasks</div>
                                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>|</span>

                                    <select
                                        name="priority"
                                        defaultValue={editingTodo?.priority || 'Medium'}
                                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', color: '#374151', fontSize: '0.9rem', background: '#f9fafb' }}
                                    >
                                        <option value="Medium">! Medium</option>
                                        <option value="High">!! High</option>
                                        <option value="Low">Low</option>
                                    </select>

                                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>|</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Due:</span>
                                        <input
                                            name="dueDate"
                                            type="date"
                                            defaultValue={editingTodo?.dueDate ? new Date(editingTodo.dueDate).toISOString().split('T')[0] : ''}
                                            style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 8px', color: '#374151', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>

                                {/* Description */}
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#111827', fontWeight: '600' }}>
                                        <AlignLeft size={18} /> Description
                                    </div>
                                    <textarea
                                        name="description"
                                        defaultValue={editingTodo?.description || ''}
                                        placeholder="Add a more detailed description..."
                                        style={{
                                            width: '100%',
                                            minHeight: '120px',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                            resize: 'vertical',
                                            fontFamily: 'inherit',
                                            lineHeight: '1.5'
                                        }}
                                    />
                                </div>

                                {/* Footer Actions */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            type="submit"
                                            style={{
                                                padding: '10px 24px',
                                                backgroundColor: '#2563eb',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {editingTodo ? 'Update Task' : 'Save Task'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowDetailTaskModal(false);
                                                setEditingTodo(null);
                                            }}
                                            style={{
                                                padding: '10px 24px',
                                                backgroundColor: '#f3f4f6',
                                                color: '#374151',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    {editingTodo && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                deleteTodo(editingTodo.id);
                                                setShowDetailTaskModal(false);
                                                setEditingTodo(null);
                                            }}
                                            style={{
                                                padding: '10px',
                                                backgroundColor: '#fee2e2',
                                                color: '#ef4444',
                                                border: 'none',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Notification Toast */}
            <AnimatePresence>
                {notification && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        style={{
                            position: 'fixed',
                            bottom: '100px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#1e293b',
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '12px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            zIndex: 3000,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontWeight: '600',
                            fontSize: '0.9rem'
                        }}
                        id="checked-toast"
                    >
                        <CheckCircle2 size={18} color="#22c55e" />
                        {notification}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CalendarView;
